import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { CheckCircle2, AlertTriangle, AlertOctagon, Wrench } from "lucide-react";
import {
  Severity,
  SEVERITY_COLOR,
  SEVERITY_LABEL,
  STATUS_LABEL,
  worstSeverity,
  lastHours,
  hourKey,
  uptimePercent,
  HourBucket,
  firstSeenAtByCat,
} from "@/lib/status";
import { toast } from "sonner";

interface Category { id: string; name: string; slug: string; description: string | null; sort_order: number; }
interface Incident {
  id: string; category_id: string; title: string; description: string | null;
  severity: Severity; status: string; is_maintenance: boolean;
  started_at: string; resolved_at: string | null; created_at: string;
}
interface Update { id: string; incident_id: string; message: string; status: string; created_at: string; }
interface Comment { id: string; incident_id: string; author_name: string; message: string; is_admin: boolean; created_at: string; }
interface Health { category_id: string; status: Severity; checked_at: string; }

function Wrap({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

export default function Status() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [updates, setUpdates] = useState<Update[]>([]);
  const [comments, setComments] = useState<Comment[]>([]);
  const [health, setHealth] = useState<Health[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const since = new Date(Date.now() - 14 * 24 * 3600 * 1000).toISOString();
    const [catsRes, incRes, hcRes] = await Promise.all([
      supabase.from("service_categories").select("*").eq("active", true).order("sort_order"),
      supabase.from("service_incidents").select("*").eq("is_public", true).gte("started_at", since).order("started_at", { ascending: false }),
      supabase.from("service_health_checks").select("category_id,status,checked_at").gte("checked_at", since),
    ]);
    setCategories((catsRes.data ?? []) as Category[]);
    const inc = (incRes.data ?? []) as Incident[];
    setIncidents(inc);
    setHealth((hcRes.data ?? []) as Health[]);

    if (inc.length) {
      const ids = inc.map((i) => i.id);
      const [upRes, coRes] = await Promise.all([
        supabase.from("incident_updates").select("*").in("incident_id", ids).order("created_at", { ascending: true }),
        supabase.from("incident_comments_public").select("id,incident_id,author_name,author_user_id,message,is_admin,created_at").in("incident_id", ids).order("created_at", { ascending: true }),
      ]);
      setUpdates((upRes.data ?? []) as Update[]);
      setComments((coRes.data ?? []) as Comment[]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const buckets = useMemo<HourBucket[]>(() => lastHours(14), []);

  // Erste bekannte Messung pro Kategorie – Stunden davor bleiben "unknown".
  const firstSeen = useMemo(() => firstSeenAtByCat(health), [health]);

  // Pro Kategorie: Map<hourKey, Severity>
  // Stunden ab firstSeen ohne Eintrag werden als "operational" angenommen
  // (Dienst lief, es wurde nur nicht gepingt). Incidents überschreiben nach worst-case.
  const hourStatusByCat = useMemo(() => {
    const map = new Map<string, Map<string, Severity>>();
    for (const cat of categories) {
      const m = new Map<string, Severity>();
      const fs = firstSeen.get(cat.id);
      if (fs) {
        for (const b of buckets) {
          if (b.date.getTime() >= fs.getTime() - 3600 * 1000) {
            m.set(b.key, "operational");
          }
        }
      }
      map.set(cat.id, m);
    }
    for (const h of health) {
      const m = map.get(h.category_id);
      if (!m) continue;
      const k = hourKey(h.checked_at);
      const cur = m.get(k);
      m.set(k, cur ? worstSeverity([cur, h.status]) : h.status);
    }
    for (const inc of incidents) {
      const m = map.get(inc.category_id);
      if (!m) continue;
      const start = new Date(inc.started_at);
      const end = inc.resolved_at ? new Date(inc.resolved_at) : new Date();
      const startH = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate(), start.getUTCHours()));
      for (let t = startH.getTime(); t <= end.getTime(); t += 3600 * 1000) {
        const k = hourKey(new Date(t));
        const cur = m.get(k) ?? "operational";
        m.set(k, worstSeverity([cur, inc.severity]));
      }
    }
    return map;
  }, [categories, health, incidents, buckets, firstSeen]);

  const currentSeverityByCat = useMemo(() => {
    const out = new Map<string, Severity>();
    for (const cat of categories) {
      const open = incidents.filter((i) => i.category_id === cat.id && i.status !== "resolved");
      if (open.length) {
        out.set(cat.id, worstSeverity(open.map((i) => i.severity)));
      } else {
        // Letzten bekannten Health-Check verwenden, nicht nur "heute"
        const catHealth = health
          .filter((h) => h.category_id === cat.id)
          .sort((a, b) => +new Date(b.checked_at) - +new Date(a.checked_at));
        out.set(cat.id, (catHealth[0]?.status as Severity) ?? "unknown");
      }
    }
    return out;
  }, [categories, incidents, health]);

  const overall: Severity = worstSeverity(Array.from(currentSeverityByCat.values()));
  const activeIncidents = incidents.filter((i) => i.status !== "resolved");
  const resolvedRecent = incidents.filter((i) => i.status === "resolved").slice(0, 10);

  const uptimeByCat = useMemo(() => {
    const out = new Map<string, number>();
    for (const cat of categories) {
      out.set(cat.id, uptimePercent(buckets, hourStatusByCat.get(cat.id) ?? new Map()));
    }
    return out;
  }, [categories, hourStatusByCat, buckets]);

  const overallUptime = useMemo(() => {
    const vals = Array.from(uptimeByCat.values());
    if (!vals.length) return 100;
    return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 100) / 100;
  }, [uptimeByCat]);

  const opCount = Array.from(currentSeverityByCat.values()).filter((s) => s === "operational").length;

  if (loading) {
    return <Wrap><div className="container py-24 text-center text-muted-foreground">Lade Statusdaten…</div></Wrap>;
  }

  const overallIcon = overall === "operational" ? CheckCircle2
    : overall === "maintenance" ? Wrench
    : overall === "major_outage" ? AlertOctagon
    : AlertTriangle;
  const OverallIcon = overallIcon;

  return (
    <Wrap>
      <Helmet>
        <title>Systemstatus — NextInvoice</title>
        <meta name="description" content="Aktueller Betriebsstatus und Verfügbarkeit aller NextInvoice Dienste der letzten 14 Tage in stündlicher Auflösung." />
        <link rel="canonical" href="https://nxtinvoice.de/status" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Systemstatus — NextInvoice",
          url: "https://nxtinvoice.de/status",
        })}</script>
      </Helmet>

      <section className="container py-12 lg:py-16">
        <div className="mb-8">
          <h1 className="font-display text-3xl lg:text-4xl font-bold mb-2">Systemstatus</h1>
          <p className="text-muted-foreground">Aktueller Betriebsstatus aller Dienste sowie Verfügbarkeit der letzten 14 Tage in stündlicher Auflösung.</p>
        </div>

        <Card className={`mb-10 p-6 lg:p-8 border-l-4 ${overall === "operational" ? "border-l-emerald-500" : overall === "major_outage" ? "border-l-red-600" : "border-l-amber-500"}`}>
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <OverallIcon className={`h-12 w-12 ${overall === "operational" ? "text-emerald-500" : overall === "major_outage" ? "text-red-600" : "text-amber-500"}`} />
              <div>
                <div className="text-2xl font-semibold">
                  {overall === "operational" ? "Alle Systeme betriebsbereit" : `Status: ${SEVERITY_LABEL[overall]}`}
                </div>
                <div className="text-sm text-muted-foreground">
                  {opCount} von {categories.length} Diensten betriebsbereit · Letzte Prüfung {new Date().toLocaleTimeString("de-DE")}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Uptime · 14 Tage</div>
              <div className="text-4xl font-bold tabular-nums">{overallUptime.toFixed(2)}<span className="text-xl text-muted-foreground"> %</span></div>
            </div>
          </div>
        </Card>

        {activeIncidents.length > 0 && (
          <div className="mb-10">
            <h2 className="text-xl font-semibold mb-4">Aktive Vorfälle</h2>
            <div className="space-y-4">
              {activeIncidents.map((inc) => (
                <IncidentCard key={inc.id} incident={inc} category={categories.find((c) => c.id === inc.category_id)} updates={updates.filter((u) => u.incident_id === inc.id)} comments={comments.filter((c) => c.incident_id === inc.id)} onCommentPosted={load} />
              ))}
            </div>
          </div>
        )}

        <div className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Dienste · 14-Tage-Verlauf (stündlich)</h2>
            <Legend />
          </div>
          <Card className="divide-y">
            {categories.map((cat) => (
              <ServiceRow
                key={cat.id}
                cat={cat}
                buckets={buckets}
                statusMap={hourStatusByCat.get(cat.id) ?? new Map()}
                currentSev={currentSeverityByCat.get(cat.id) ?? "unknown"}
                uptime={uptimeByCat.get(cat.id) ?? 100}
              />
            ))}
          </Card>
        </div>

        {resolvedRecent.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold mb-4">Zuletzt behoben</h2>
            <div className="space-y-3">
              {resolvedRecent.map((inc) => (
                <IncidentCard key={inc.id} incident={inc} category={categories.find((c) => c.id === inc.category_id)} updates={updates.filter((u) => u.incident_id === inc.id)} comments={comments.filter((c) => c.incident_id === inc.id)} onCommentPosted={load} compact />
              ))}
            </div>
          </div>
        )}
      </section>
    </Wrap>
  );
}

function Legend() {
  const items: Severity[] = ["operational", "degraded", "partial_outage", "major_outage", "maintenance", "unknown"];
  return (
    <div className="hidden md:flex items-center gap-3 text-xs text-muted-foreground">
      {items.map((s) => (
        <div key={s} className="flex items-center gap-1.5">
          <span className={`inline-block w-3 h-3 rounded-sm ${SEVERITY_COLOR[s]}`} />
          <span>{SEVERITY_LABEL[s]}</span>
        </div>
      ))}
    </div>
  );
}

function ServiceRow({ cat, buckets, statusMap, currentSev, uptime }: {
  cat: Category;
  buckets: HourBucket[];
  statusMap: Map<string, Severity>;
  currentSev: Severity;
  uptime: number;
}) {
  return (
    <div className="p-4 lg:p-5">
      <div className="flex items-center justify-between gap-4 mb-3">
        <div className="min-w-0">
          <div className="font-medium">{cat.name}</div>
          {cat.description && <div className="text-xs text-muted-foreground truncate">{cat.description}</div>}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-sm tabular-nums text-muted-foreground">{uptime.toFixed(2)} %</span>
          <Badge className={`${SEVERITY_COLOR[currentSev]} text-white border-0`}>{SEVERITY_LABEL[currentSev]}</Badge>
        </div>
      </div>
      <div className="flex items-stretch gap-[1px] h-9">
        {buckets.map((b, idx) => {
          const s = statusMap.get(b.key) ?? "unknown";
          const dayGap = b.isDayBoundary && idx > 0;
          const tip = `${b.date.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })} — ${
            s === "unknown" ? "noch keine Messdaten" : SEVERITY_LABEL[s]
          }`;
          return (
            <div
              key={b.key}
              title={tip}
              className={`flex-1 min-w-[2px] rounded-[1px] ${SEVERITY_COLOR[s]} ${s === "unknown" ? "opacity-40" : "opacity-90 hover:opacity-100"} transition ${dayGap ? "ml-[2px]" : ""}`}
            />
          );
        })}
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground mt-1.5">
        <span>vor 14 Tagen</span>
        <span>vor 7 Tagen</span>
        <span>vor 24h</span>
        <span>jetzt</span>
      </div>
    </div>
  );
}

function IncidentCard({ incident, category, updates, comments, onCommentPosted, compact }: {
  incident: Incident;
  category?: Category;
  updates: Update[];
  comments: Comment[];
  onCommentPosted: () => void;
  compact?: boolean;
}) {
  const [open, setOpen] = useState(!compact);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("post-incident-comment", {
        body: { incident_id: incident.id, name, email, message, website },
      });
      if (error || (data as any)?.error) {
        toast.error((data as any)?.error || "Konnte Kommentar nicht senden");
      } else {
        toast.success("Kommentar veröffentlicht");
        setName(""); setEmail(""); setMessage("");
        onCommentPosted();
      }
    } finally { setSubmitting(false); }
  };

  return (
    <Card className="p-5">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-start justify-between gap-3 text-left">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Badge className={`${SEVERITY_COLOR[incident.severity]} text-white border-0`}>{SEVERITY_LABEL[incident.severity]}</Badge>
            <Badge variant="outline">{STATUS_LABEL[incident.status] || incident.status}</Badge>
            {category && <span className="text-xs text-muted-foreground">{category.name}</span>}
          </div>
          <div className="font-medium">{incident.title}</div>
          <div className="text-xs text-muted-foreground">
            Begonnen: {new Date(incident.started_at).toLocaleString("de-DE")}
            {incident.resolved_at && ` · Behoben: ${new Date(incident.resolved_at).toLocaleString("de-DE")}`}
          </div>
        </div>
        <span className="text-xs text-muted-foreground">{open ? "Schließen" : "Details"}</span>
      </button>

      {open && (
        <div className="mt-4 space-y-4">
          {incident.description && <p className="text-sm">{incident.description}</p>}

          {updates.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-muted-foreground mb-2">VERLAUF</div>
              <div className="space-y-2">
                {updates.map((u) => (
                  <div key={u.id} className="border-l-2 border-primary/30 pl-3 text-sm">
                    <div className="text-xs text-muted-foreground">{new Date(u.created_at).toLocaleString("de-DE")} · {STATUS_LABEL[u.status] || u.status}</div>
                    <div>{u.message}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-2">KOMMENTARE ({comments.length})</div>
            <div className="space-y-2 mb-4">
              {comments.map((c) => (
                <div key={c.id} className={`p-3 rounded-md text-sm ${c.is_admin ? "bg-primary/5 border border-primary/20" : "bg-muted/50"}`}>
                  <div className="text-xs text-muted-foreground mb-1">
                    <span className="font-medium text-foreground">{c.author_name}</span>
                    {c.is_admin && <Badge variant="secondary" className="ml-2 text-[10px]">Team</Badge>}
                    <span className="ml-2">{new Date(c.created_at).toLocaleString("de-DE")}</span>
                  </div>
                  <div className="whitespace-pre-wrap">{c.message}</div>
                </div>
              ))}
              {comments.length === 0 && <div className="text-xs text-muted-foreground">Noch keine Kommentare.</div>}
            </div>

            <form onSubmit={submit} className="space-y-3 border-t pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor={`n-${incident.id}`}>Name *</Label>
                  <Input id={`n-${incident.id}`} value={name} onChange={(e) => setName(e.target.value)} required maxLength={80} />
                </div>
                <div>
                  <Label htmlFor={`e-${incident.id}`}>E-Mail (optional, nicht öffentlich)</Label>
                  <Input id={`e-${incident.id}`} type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={200} />
                </div>
              </div>
              <div>
                <Label htmlFor={`m-${incident.id}`}>Nachricht *</Label>
                <Textarea id={`m-${incident.id}`} value={message} onChange={(e) => setMessage(e.target.value)} required maxLength={1000} rows={3} />
              </div>
              {/* Honeypot */}
              <input type="text" value={website} onChange={(e) => setWebsite(e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" />
              <Button type="submit" size="sm" disabled={submitting}>{submitting ? "Sende…" : "Kommentar abschicken"}</Button>
            </form>
          </div>
        </div>
      )}
    </Card>
  );
}